import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import { Message } from 'primereact/message';
import { toast } from 'react-toastify';
import backendVerifiedUser  from '../../api/backendVerifiedUser';

export default function OrderNumberSettings() {
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState('');
  const [currentSettings, setCurrentSettings] = useState({
    order_number_format: '[PHONE]-[INCREMENT]',
    order_number_increment: 1
  });

  const formik = useFormik({
    initialValues: {
      order_number_format: '[PHONE]-[INCREMENT]',
      order_number_increment: 1,
      preview_phone: '2721127'
    },
    validationSchema: Yup.object({
      order_number_format: Yup.string()
        .required('Order number format is required')
        .test('contains-increment', 'Format must contain [INCREMENT] placeholder', value => 
          value && value.includes('[INCREMENT]')
        )
        .max(100, 'Format must be 100 characters or less'),
      order_number_increment: Yup.number()
        .required('Starting increment is required')
        .min(1, 'Increment must be at least 1')
        .integer('Increment must be a whole number'),
      preview_phone: Yup.string()
        .required('Phone number is required for preview')
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        // Update format
        const formatResponse = await backendVerifiedUser.put('/order-number/format', {
          format: values.order_number_format
        });

        if (formatResponse.data.success) {
          // Update increment
          const incrementResponse = await backendVerifiedUser.put('/order-number/increment', {
            increment: values.order_number_increment
          });

          if (incrementResponse.data.success) {
            toast.success('Order number settings updated successfully!');
            fetchCurrentSettings();
          } else {
            toast.error('Failed to update increment');
          }
        } else {
          toast.error('Failed to update format');
        }
      } catch (error) {
        console.error('Error updating settings:', error);
        toast.error(error.response?.data?.error?.msg || 'Failed to update settings');
      } finally {
        setLoading(false);
      }
    }
  });

  const fetchCurrentSettings = async () => {
    try {
      const response = await backendVerifiedUser.get('/order-number/settings');
      if (response.data.success) {
        const settings = response.data.success;
        setCurrentSettings(settings);
        formik.setValues({
          order_number_format: settings.order_number_format,
          order_number_increment: settings.order_number_increment,
          preview_phone: '2721127'
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load current settings');
    }
  };

  const generatePreview = async () => {
    if (!formik.values.order_number_format || !formik.values.preview_phone) {
      return;
    }

    setPreviewLoading(true);
    try {
      const response = await backendVerifiedUser.post('/order-number/preview', {
        format: formik.values.order_number_format,
        phone: formik.values.preview_phone
      });

      if (response.data.success) {
        setPreview(response.data.success.preview);
      } else {
        toast.error('Failed to generate preview');
      }
    } catch (error) {
      console.error('Error generating preview:', error);
      toast.error(error.response?.data?.error?.msg || 'Failed to generate preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    fetchCurrentSettings();
  }, []);

  useEffect(() => {
    if (formik.values.order_number_format && formik.values.preview_phone) {
      generatePreview();
    }
  }, [formik.values.order_number_format, formik.values.preview_phone]);

  const formatExamples = [
    { label: 'Phone + Increment', value: '[PHONE]-[INCREMENT]', description: 'e.g., 2721127-001' },
    { label: 'Custom Prefix + Increment', value: '[2721127]-[INCREMENT]', description: 'e.g., 2721127-001' },
    { label: 'Company Code + Increment', value: 'PC-[INCREMENT]', description: 'e.g., PC-001' },
    { label: 'Year + Increment', value: '2024-[INCREMENT]', description: 'e.g., 2024-001' }
  ];

  return (
    <div className="mx-4">
      <div className="card shadow-md rounded-lg p-4 mt-3">
        <Divider>
          <span className="text-2xl font-bold p-font-style text-center text-black mx-1">
            Order Number Configuration
          </span>
        </Divider>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Settings Form */}
          <div>
            <form onSubmit={formik.handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="order_number_format" className="block text-sm font-medium text-gray-700 mb-2">
                    Order Number Format *
                  </label>
                  <InputText
                    id="order_number_format"
                    name="order_number_format"
                    className="w-full"
                    value={formik.values.order_number_format}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="[PHONE]-[INCREMENT]"
                  />
                  {formik.touched.order_number_format && formik.errors.order_number_format && (
                    <div className="text-red-500 text-sm mt-1">{formik.errors.order_number_format}</div>
                  )}
                  <small className="text-gray-600">
                    Use [PHONE] for customer phone number and [INCREMENT] for sequential number. 
                    You can also use custom prefixes like [2721127] for a fixed prefix.
                  </small>
                </div>

                <div>
                  <label htmlFor="order_number_increment" className="block text-sm font-medium text-gray-700 mb-2">
                    Starting Increment Number *
                  </label>
                  <InputNumber
                    id="order_number_increment"
                    name="order_number_increment"
                    className="w-full"
                    value={formik.values.order_number_increment}
                    onValueChange={(e) => formik.setFieldValue('order_number_increment', e.value)}
                    min={1}
                    placeholder="1"
                  />
                  {formik.touched.order_number_increment && formik.errors.order_number_increment && (
                    <div className="text-red-500 text-sm mt-1">{formik.errors.order_number_increment}</div>
                  )}
                  <small className="text-gray-600">
                    The starting number for the increment sequence. Next order will use this number + 1.
                  </small>
                </div>

                <div className="flex gap-2">
                  <Button
                    type="submit"
                    label="Save Settings"
                    icon="pi pi-save"
                    className="p-primary-btn"
                    loading={loading}
                    disabled={!formik.isValid}
                  />
                  <Button
                    type="button"
                    label="Reset to Default"
                    icon="pi pi-refresh"
                    className="p-secondary-btn"
                    onClick={() => {
                      formik.setValues({
                        order_number_format: '[PHONE]-[INCREMENT]',
                        order_number_increment: 1,
                        preview_phone: '2721127'
                      });
                    }}
                  />
                </div>
              </div>
            </form>
          </div>

          {/* Preview Section */}
          <div>
            <Card title="Preview" className="h-full">
              <div className="space-y-4">
                <div>
                  <label htmlFor="preview_phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Test Phone Number
                  </label>
                  <InputText
                    id="preview_phone"
                    name="preview_phone"
                    className="w-full"
                    value={formik.values.preview_phone}
                    onChange={formik.handleChange}
                    placeholder="2721127"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Generated Order Number
                  </label>
                  <div className="p-3 bg-gray-50 rounded border">
                    {previewLoading ? (
                      <div className="flex items-center">
                        <i className="pi pi-spin pi-spinner mr-2"></i>
                        Generating preview...
                      </div>
                    ) : preview ? (
                      <span className="font-mono text-lg font-bold text-blue-600">{preview}</span>
                    ) : (
                      <span className="text-gray-500">Enter format and phone number to see preview</span>
                    )}
                  </div>
                </div>

                <Message 
                  severity="info" 
                  text="The preview shows how the next order number will look with your current settings."
                  className="w-full"
                />
              </div>
            </Card>
          </div>
        </div>

        {/* Format Examples */}
        <div className="mt-6">
          <Divider>
            <span className="text-lg font-semibold text-gray-700">
              Format Examples
            </span>
          </Divider>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {formatExamples.map((example, index) => (
              <Card key={index} className="cursor-pointer hover:shadow-md transition-shadow" 
                    onClick={() => formik.setFieldValue('order_number_format', example.value)}>
                <div className="p-3">
                  <h4 className="font-semibold text-gray-800 mb-1">{example.label}</h4>
                  <code className="text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded">{example.value}</code>
                  <p className="text-sm text-gray-600 mt-2">{example.description}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Current Settings Display */}
        <div className="mt-6">
          <Divider>
            <span className="text-lg font-semibold text-gray-700">
              Current Settings
            </span>
          </Divider>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <Card>
              <div className="p-3">
                <h4 className="font-semibold text-gray-800 mb-2">Current Format</h4>
                <code className="text-sm text-green-600 bg-green-50 px-2 py-1 rounded">
                  {currentSettings.order_number_format}
                </code>
              </div>
            </Card>
            
            <Card>
              <div className="p-3">
                <h4 className="font-semibold text-gray-800 mb-2">Next Increment</h4>
                <span className="text-lg font-bold text-blue-600">
                  {currentSettings.order_number_increment}
                </span>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}